/* eslint-disable react-refresh/only-export-components */
/**
 * Renders a specimen whose source is markdown plus kit fences.
 * Core (callout, pros-cons, properties) always renders.
 * Review (table, checklist) renders only when the note lists `review` under `kits:`.
 */

import { useMemo, useState, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { parse as parseYaml } from 'yaml'
import { testIds } from '@/lib/test-ids'

type FenceBlock = {
  kind: 'fence'
  name: string
  raw: string
  data: Record<string, unknown> | null
}

type DocBlock = { kind: 'md'; text: string } | FenceBlock

const CORE = new Set(['callout', 'pros-cons', 'properties'])
const REVIEW = new Set(['table', 'checklist'])
const TONES = new Set(['danger', 'warning', 'success'])
const PILL_KEYS = new Set(['pri', 'status', 'result', 'outcome'])

function columnKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function filterSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function asText(value: unknown): string {
  if (value == null) return ''
  return String(value)
}

function asList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => asText(item))
}

export function readKits(source: string): string[] {
  const match = source.match(/^---\r?\n([\s\S]*?)\r?\n---/)
  const kits = new Set<string>(['core'])
  if (!match) return ['core']
  try {
    const data = parseYaml(match[1]) as { kits?: unknown }
    if (Array.isArray(data?.kits)) {
      for (const kit of data.kits) kits.add(String(kit))
    }
  } catch {
    return ['core']
  }
  return [...kits]
}

export function bodyWithoutFrontmatter(source: string): string {
  return source.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '')
}

export function parseStructuredDoc(source: string): DocBlock[] {
  const body = bodyWithoutFrontmatter(source)
  const fenceRe = /```yaml ([a-z0-9-]+)\n([\s\S]*?)\n```/g
  const blocks: DocBlock[] = []
  let cursor = 0
  for (const match of body.matchAll(fenceRe)) {
    const index = match.index ?? 0
    const text = body.slice(cursor, index)
    if (text.trim()) blocks.push({ kind: 'md', text })
    const name = match[1]
    let data: Record<string, unknown> | null = null
    try {
      const parsed = parseYaml(match[2])
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        data = parsed as Record<string, unknown>
      }
    } catch {
      data = null
    }
    blocks.push({ kind: 'fence', name, raw: match[0], data })
    cursor = index + match[0].length
  }
  const rest = body.slice(cursor)
  if (rest.trim()) blocks.push({ kind: 'md', text: rest })
  return blocks
}

export function toggleChecklistItem(source: string, fenceIndex: number, itemIndex: number): string {
  const re = /```yaml checklist\n[\s\S]*?\n```/g
  let match: RegExpExecArray | null
  let seen = 0
  while ((match = re.exec(source))) {
    if (seen !== fenceIndex) {
      seen += 1
      continue
    }
    let item = 0
    const next = match[0].replace(/done:\s*(true|false)/g, (full, value: string) => {
      if (item !== itemIndex) {
        item += 1
        return full
      }
      item += 1
      return value === 'true' ? 'done: false' : 'done: true'
    })
    return source.slice(0, match.index) + next + source.slice(match.index + match[0].length)
  }
  return source
}

function fenceReady(name: string, kits: string[], data: Record<string, unknown> | null): boolean {
  if (!data) return false
  if (CORE.has(name)) return kits.includes('core')
  if (REVIEW.has(name)) return kits.includes('review')
  return false
}

function InlineMarkdown({ text }: { text: string }) {
  if (!text.includes('[') && !text.includes('`')) return <>{text}</>
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{ p: ({ children }) => <>{children}</> }}
    >
      {text}
    </ReactMarkdown>
  )
}

function UnimportedFence({ message, source }: { message: string; source: string }) {
  return (
    <div className="sdoc-fence" data-testid={testIds.structuredDocBlocks.unimported}>
      <div className="sdoc-fence-label">{message}</div>
      <pre>{source}</pre>
    </div>
  )
}

function KitCallout({ data }: { data: Record<string, unknown> }) {
  const tone = asText(data.tone)
  const toneClass = TONES.has(tone) ? ` sdoc-callout-${tone}` : ''
  return (
    <div className={`sdoc-callout${toneClass}`}>
      {data.lead ? <p className="sdoc-callout-lead">{asText(data.lead)}</p> : null}
      <p><InlineMarkdown text={asText(data.body)} /></p>
    </div>
  )
}

function KitProsCons({ data }: { data: Record<string, unknown> }) {
  const pros = asList(data.pros)
  const cons = asList(data.cons)
  return (
    <div className="sdoc-proscons">
      <div className="sdoc-pros">
        <h3>{asText(data.pros_label) || 'Pros'}</h3>
        <ul>
          {pros.map((item) => <li key={item}><InlineMarkdown text={item} /></li>)}
        </ul>
      </div>
      <div className="sdoc-cons">
        <h3>{asText(data.cons_label) || 'Cons'}</h3>
        <ul>
          {cons.map((item) => <li key={item}><InlineMarkdown text={item} /></li>)}
        </ul>
      </div>
    </div>
  )
}

function KitProperties({ data }: { data: Record<string, unknown> }) {
  const items = Array.isArray(data.items) ? data.items : []
  return (
    <dl className="sdoc-properties">
      {items.map((item) => {
        const row = item as Record<string, unknown>
        const label = asText(row.label)
        return (
          <div key={label}>
            <dt>{label.includes('`') || label.includes('/') ? <InlineMarkdown text={label} /> : label}</dt>
            <dd><InlineMarkdown text={asText(row.value)} /></dd>
          </div>
        )
      })}
    </dl>
  )
}

function readColumns(data: Record<string, unknown>): Array<{ key: string; label: string }> {
  if (!Array.isArray(data.columns)) return []
  return data.columns.flatMap((col) => {
    if (typeof col === 'string') return [{ key: columnKey(col), label: col }]
    if (!col || typeof col !== 'object') return []
    const rec = col as Record<string, unknown>
    const label = asText(rec.label ?? rec.key)
    const key = asText(rec.key) || columnKey(label)
    return key ? [{ key, label }] : []
  })
}

function toneForCell(row: Record<string, unknown>, key: string, value: string): string | undefined {
  const explicit = asText(row.tone)
  if (PILL_KEYS.has(key) && TONES.has(explicit)) return explicit
  if (key === 'pri' && value === 'P0') return 'danger'
  if (key === 'pri' && value === 'P1') return 'warning'
  return undefined
}

function KitTable({
  data,
  fenceIndex,
  filterValue,
  onFilter,
}: {
  data: Record<string, unknown>
  fenceIndex: number
  filterValue: string
  onFilter: (value: string) => void
}) {
  const columns = readColumns(data)
  const rows = Array.isArray(data.rows) ? data.rows as Array<Record<string, unknown>> : []
  const filterKey = asText(data.filter)
  const options = filterKey
    ? ['All', ...rows.map((row) => asText(row[filterKey])).filter((value, index, all) => value && all.indexOf(value) === index)]
    : []
  const shown = !filterKey || filterValue === 'All'
    ? rows
    : rows.filter((row) => asText(row[filterKey]) === filterValue)

  return (
    <>
      {options.length > 0 ? (
        <div className="sdoc-segmented" data-testid={testIds.structuredDocBlocks.filters}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={filterValue === option}
              data-testid={testIds.structuredDocBlocks.filter(`${fenceIndex}-${filterSlug(option)}`)}
              onClick={() => onFilter(option)}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
      <div className="table-scroll-wrapper">
        <table className="sdoc-table">
          <thead>
            <tr>
              {columns.map((col) => <th key={col.key}>{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, index) => (
              <tr key={`${asText(row[columns[0]?.key])}-${index}`}>
                {columns.map((col) => {
                  const value = asText(row[col.key])
                  const tone = toneForCell(row, col.key, value)
                  let content: ReactNode = <InlineMarkdown text={value} />
                  if (col.key === 'file' && !value.includes('[')) content = <code>{value}</code>
                  if (tone) content = <span className={`sdoc-tone sdoc-tone-${tone}`}>{value}</span>
                  return <td key={col.key}>{content}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function KitChecklist({
  data,
  onToggle,
}: {
  data: Record<string, unknown>
  onToggle: (itemIndex: number) => void
}) {
  const items = Array.isArray(data.items) ? data.items as Array<Record<string, unknown>> : []
  return (
    <ul className="sdoc-checklist">
      {items.map((item, index) => {
        const text = asText(item.text)
        const done = item.done === true || item.done === 'true'
        return (
          <li key={text}>
            <input
              type="checkbox"
              checked={done}
              aria-label={text}
              onChange={() => onToggle(index)}
            />
            <span>
              <span className="sdoc-check-text"><InlineMarkdown text={text} /></span>
              {item.meta ? (
                <span className="sdoc-check-meta"><InlineMarkdown text={asText(item.meta)} /></span>
              ) : null}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export function StructuredDocMarkdown({
  source,
  resetKey,
  onChecklistToggle,
}: {
  source: string
  resetKey: string
  onChecklistToggle: (fenceIndex: number, itemIndex: number) => void
}) {
  const kits = useMemo(() => readKits(source), [source])
  const blocks = useMemo(() => parseStructuredDoc(source), [source])
  const [filterState, setFilterState] = useState<{ key: string; values: Record<number, string> }>({
    key: resetKey,
    values: {},
  })
  const filters = filterState.key === resetKey ? filterState.values : {}

  let tableIndex = -1
  let checklistIndex = -1

  return (
    <>
      {blocks.map((block, index) => {
        if (block.kind === 'md') {
          return <ReactMarkdown key={index} remarkPlugins={[remarkGfm]}>{block.text}</ReactMarkdown>
        }
        if (!block.data) {
          return <UnimportedFence key={index} message={`This ${block.name} fence did not parse.`} source={block.raw} />
        }
        if (!fenceReady(block.name, kits, block.data)) {
          return (
            <UnimportedFence
              key={index}
              message={`This ${block.name} fence stays source until its kit is imported.`}
              source={block.raw}
            />
          )
        }
        if (block.name === 'callout') return <KitCallout key={index} data={block.data} />
        if (block.name === 'pros-cons') return <KitProsCons key={index} data={block.data} />
        if (block.name === 'properties') return <KitProperties key={index} data={block.data} />
        if (block.name === 'table') {
          tableIndex += 1
          const id = tableIndex
          return (
            <KitTable
              key={index}
              data={block.data}
              fenceIndex={id}
              filterValue={filters[id] ?? 'All'}
              onFilter={(value) => setFilterState({ key: resetKey, values: { ...filters, [id]: value } })}
            />
          )
        }
        if (block.name === 'checklist') {
          checklistIndex += 1
          const id = checklistIndex
          return (
            <KitChecklist
              key={index}
              data={block.data}
              onToggle={(itemIndex) => onChecklistToggle(id, itemIndex)}
            />
          )
        }
        return <UnimportedFence key={index} message={`No renderer for ${block.name}.`} source={block.raw} />
      })}
    </>
  )
}
